import { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ViewColumnIcon from '@mui/icons-material/ViewColumnOutlined';
import Button from '@mui/material/Button';
import EditCalendarIcon from '@mui/icons-material/EditCalendarOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import { ReleaseCalDialog } from './ReleaseCalDialog';
import { CapacityDialog } from './CapacityDialog';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useModel } from '../hooks/useModel';
import { useColumns } from '../hooks/useColumns';
import { useConfig } from '../store/config';
import { useFilters } from '../store/filters';
import { ColumnsCard } from './columns/ColumnsCard';
import { sortReleasesByStart, colorForLane } from '../lib/releases';
import { getKnownReleases, detectRelease } from '../lib/release-detect';
import { fmtInt, formatDate } from '../lib/format';
import { isDone } from '../lib/status';
import type { Issue } from '../types/issue';
import { useT, format } from '../i18n';

// Pro Release ein expandierbarer Block mit Header-Badges (Plan / Ist /
// Kapazität / Auslastung) und der Säulen-Card. Header zählt die Items,
// die nach aktuellem Filter UND zum jeweiligen Release gehören.
export function ReleaseBlocks() {
  const rowCount    = useFilteredRows().length;
  const releaseCal  = useConfig(s => s.releaseCal);
  const [relCalOpen, setRelCalOpen] = useState(false);
  const [capaOpen, setCapaOpen]     = useState(false);

  const releases = useMemo(() => sortReleasesByStart(releaseCal), [releaseCal]);
  const t = useT();
  if (!rowCount || !releases.length) return null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <ViewColumnIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">{t.releaseBlocks.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {format(t.releaseBlocks.subtitle, { count: releases.length })}
            </Typography>
          </Box>
          <Button size="small" startIcon={<EditCalendarIcon />} onClick={() => setRelCalOpen(true)}>
            {t.releaseBlocks.releaseCalBtn}
          </Button>
          <Button size="small" startIcon={<GroupsIcon />} onClick={() => setCapaOpen(true)}>
            {t.releaseBlocks.capacityBtn}
          </Button>
        </Stack>

        <Stack spacing={1.5}>
          {releases.map((name, i) => (
            <ReleaseBlock key={name} name={name} color={colorForLane(i)} />
          ))}
        </Stack>
      </CardContent>

      <ReleaseCalDialog open={relCalOpen} onClose={() => setRelCalOpen(false)} />
      <CapacityDialog   open={capaOpen}   onClose={() => setCapaOpen(false)} />
    </Card>
  );
}

function ReleaseBlock({ name, color }: { name: string; color: string }) {
  const [open, setOpen] = useState(false);
  const cols = useColumns(name);
  const model = useModel();
  const visible = useFilteredRows();
  const teamFilter = useFilters(s => s.team);
  const releaseCal = useConfig(s => s.releaseCal);
  const t = useT();

  // Counts & Plan/Ist pro Block — über die *gefilterten* Items dieses Releases
  const stats = useMemo(() => {
    const known = getKnownReleases(Object.keys(releaseCal));
    const visKeys = new Set(visible.map(r => r['Issue key']));
    let nEpics = 0, nStories = 0, nBugs = 0, planSum = 0, istSum = 0;
    for (const e of model.epics) {
      if (e._isOrphanContainer) continue;
      if (detectRelease(e, known) !== name) continue;
      const kids: Issue[] = (e._kids ?? []).filter((k: Issue) =>
        (!teamFilter || k['Team Name'] === teamFilter) && visKeys.has(k['Issue key'])
      );
      if (visKeys.has(e['Issue key']) || kids.length) nEpics++;
      for (const k of kids) {
        if (k['Issue Type'] === 'Story') nStories++;
        else if (k['Issue Type'] === 'Bug') nBugs++;
      }
      // Plan/Ist nur über sichtbare Kids
      for (const k of kids) {
        const kk = k as Issue & { _plan?: { val: number }; _actual?: { val: number } };
        planSum += kk._plan?.val ?? 0;
        if (isDone(k['Status'] ?? '')) istSum += kk._actual?.val ?? 0;
        else istSum += kk._plan?.val ?? 0;
      }
    }
    return { nEpics, nStories, nBugs, planSum, istSum };
  }, [model, visible, teamFilter, releaseCal, name]);

  const cap   = cols.find(c => c.key === 'kapa' || c.key === 'kapaPlan');
  const capTotal = cap ? Math.round(cap.stories) + Math.round(cap.bugs) : 0;
  const usage = capTotal > 0 ? stats.planSum / capTotal : 0;
  const usageColor: 'success' | 'warning' | 'error' | 'default' =
    capTotal > 0
      ? usage > 1 ? 'error' : usage > 0.85 ? 'warning' : 'success'
      : 'default';

  const cfg = releaseCal[name];
  const dateLabel = cfg?.start && cfg?.end
    ? `${formatDate(cfg.start)} → ${formatDate(cfg.end)}`
    : t.releaseBlocks.noDateSet;

  return (
    <Accordion
      expanded={open}
      onChange={(_, v) => setOpen(v)}
      disableGutters
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        borderLeft: `4px solid ${color}`,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ color, fontWeight: 700 }}>{name}</Typography>
          <Typography variant="caption" color="text.secondary">
            📅 {dateLabel} · {format(t.releaseBlocks.itemsSummary, { epics: stats.nEpics, stories: stats.nStories, bugs: stats.nBugs })}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" label={format(t.releaseBlocks.planChip, { value: fmtInt(stats.planSum) })} color="primary" variant="outlined" />
          <Chip size="small" label={format(t.releaseBlocks.actualChip, { value: fmtInt(stats.istSum) })} color="secondary" variant="outlined" />
          <Chip
            size="small"
            label={capTotal > 0
              ? format(t.releaseBlocks.capacityChip, { value: fmtInt(capTotal), pct: Math.round(usage * 100) })
              : t.releaseBlocks.noCapacityChip}
            color={usageColor}
            variant={capTotal > 0 ? 'filled' : 'outlined'}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <ColumnsCard columns={cols} />
      </AccordionDetails>
    </Accordion>
  );
}
